/**
 * Static catalog of CMW feature flags, sourced from control.smartrent.com
 * (`ControlRoom.FeatureFlags.allowed_flags/0` in
 * apps/control_room/lib/control_room/feature_flags.ex) and the call sites
 * that check each flag.
 *
 * The CMW API only returns flag names and gates — descriptions are not stored
 * anywhere server-side, so this catalog is the source of truth for what each
 * flag does. Flags returned by the API but missing here surface on the
 * insights dashboard as "Undocumented".
 *
 * Last synced with control.smartrent.com: 2026-07-08 (83 flags).
 */

export const FLAG_CATEGORIES = [
  "Access Control",
  "Climate",
  "CRM & Tours",
  "Devices & Hubs",
  "Integrations",
  "Platform",
  "Reports",
  "Residents",
  "Support & Chat",
  "UI",
  "Work / SMRT Core",
] as const;

export type FlagCategory = (typeof FLAG_CATEGORIES)[number];

export interface FlagCatalogEntry {
  description: string;
  category: FlagCategory;
}

export const FLAG_CATALOG: Record<string, FlagCatalogEntry> = {
  example: {
    description:
      "Placeholder flag used to test the feature flag system itself. Safe to ignore.",
    category: "Platform",
  },
  occupied_thermostat_schedule: {
    description: "Thermostat scheduling for occupied units.",
    category: "Climate",
  },
  resident_default_thermostat_schedules: {
    description:
      "Applies default thermostat schedules for residents when they move in.",
    category: "Climate",
  },
  yardi_rooms_enabled: {
    description: "Yardi rooms support in the Yardi PMS integration.",
    category: "Integrations",
  },
  livly: {
    description: "Shows the Livly integration tile on unit pages.",
    category: "Integrations",
  },
  resman_amimo: {
    description: "ResMan/Amimo PMS integration.",
    category: "Integrations",
  },
  sight_plan_service_requests: {
    description: "Syncs service requests with SightPlan.",
    category: "Integrations",
  },
  parking_pm_syncs: {
    description: "Parking property-management syncs.",
    category: "Integrations",
  },
  venterra_org_creds: {
    description: "Venterra organization credentials integration.",
    category: "Integrations",
  },
  enable_search_dictionary_refresh: {
    description: "Enables the search dictionary refresh background job.",
    category: "Platform",
  },
  sight_plan_broadcast_messages: {
    description: "Broadcast messages via SightPlan.",
    category: "Integrations",
  },
  sight_plan_packages: {
    description: "Package tracking sync with SightPlan.",
    category: "Integrations",
  },
  entrata_marketplace_tile: {
    description: "Shows the Entrata marketplace tile on unit pages.",
    category: "Integrations",
  },
  org_show_avalon_bay: {
    description:
      "Shows the AvalonBay provider option in third-party application providers for the org.",
    category: "Integrations",
  },
  org_show_harbor_group: {
    description:
      "Shows the Harbor Group provider option in third-party application providers for the org.",
    category: "Integrations",
  },
  org_show_rent_manager: {
    description:
      "Shows the Rent Manager provider option in third-party application providers for the org.",
    category: "Integrations",
  },
  org_show_lennar: {
    description:
      "Shows the Lennar provider option in third-party application providers for the org.",
    category: "Integrations",
  },
  org_show_rent_roll: {
    description:
      "Shows the Rent Roll provider option in third-party application providers for the org.",
    category: "Integrations",
  },
  org_show_lift_master: {
    description:
      "Shows the LiftMaster provider option in third-party application providers for the org.",
    category: "Integrations",
  },
  enable_custom_code_creation_yale_ble: {
    description: "Custom access code creation on Yale BLE locks.",
    category: "Access Control",
  },
  jammed_lock_report: {
    description: "Jammed lock report for organizations.",
    category: "Reports",
  },
  energystar_enabled: {
    description: "EnergyStar integration and resident EnergyStar emails.",
    category: "Integrations",
  },
  intercom_chat: {
    description: "Intercom support chat widget in CMW for the org.",
    category: "Support & Chat",
  },
  zendesk_chat: {
    description: "Zendesk support chat widget in CMW for the org.",
    category: "Support & Chat",
  },
  device_battery_history: {
    description: "Records device battery history for reporting.",
    category: "Devices & Hubs",
  },
  smrt_core: {
    description:
      "SMRT Core (Work) platform features — the primary flag gating Work functionality for a group/org.",
    category: "Work / SMRT Core",
  },
  vacant_access: {
    description: "Vacant unit access codes.",
    category: "Access Control",
  },
  amenities: {
    description: "Amenities feature and amenity notifications.",
    category: "Residents",
  },
  stuck_codes: {
    description: "Stuck access-code detection and notifications.",
    category: "Access Control",
  },
  enable_stuck_codes_report: {
    description: "Stuck codes report in organization reports.",
    category: "Reports",
  },
  allow_in_app_provisioning: {
    description: "In-app device provisioning for residents.",
    category: "Devices & Hubs",
  },
  hardware_ordering: {
    description: "Hardware ordering flow and report.",
    category: "Platform",
  },
  netsuite_hardware_ordering: {
    description: "NetSuite-backed hardware ordering integration.",
    category: "Integrations",
  },
  resident_zendesk_chat: {
    description: "Zendesk support chat in the resident app.",
    category: "Support & Chat",
  },
  resident_intercom_chat: {
    description: "Intercom support chat in the resident app.",
    category: "Support & Chat",
  },
  resident_support_tickets: {
    description: "Support tickets in the resident app.",
    category: "Support & Chat",
  },
  resident_support_nudges: {
    description: "Support nudges in the resident app.",
    category: "Support & Chat",
  },
  time_based_access_codes: {
    description: "Time-based access codes for groups and vendors.",
    category: "Access Control",
  },
  community_lockdown: {
    description: "Community lockdown feature and its notifications.",
    category: "Access Control",
  },
  zipato_firmware_updates: {
    description: "Zipato hub firmware update background job.",
    category: "Devices & Hubs",
  },
  apple_wallet: {
    description: "Apple Wallet passes for access codes.",
    category: "Access Control",
  },
  smart_iq_dev: {
    description: "SmartIQ development channel.",
    category: "Platform",
  },
  hvac_energy_report: {
    description:
      "HVAC energy report. Enabling for a group also triggers an initial energy data fetch job.",
    category: "Reports",
  },
  crm_enabled: {
    description: "CRM backend features (e.g. tour task workers).",
    category: "CRM & Tours",
  },
  crm_ui_enabled: {
    description: "CRM UI in CMW.",
    category: "CRM & Tours",
  },
  migrate_zipato_hubs_to_v2: {
    description: "Zipato hub v2 migration background job.",
    category: "Devices & Hubs",
  },
  device_attribute_confirmation: {
    description: "Device attribute confirmation on device integrations.",
    category: "Devices & Hubs",
  },
  tours_v2_disabled: {
    description: "Kill switch that disables Tours v2 when enabled.",
    category: "CRM & Tours",
  },
  resync_device_slot: {
    description: "Resyncs device user-code slots.",
    category: "Access Control",
  },
  schedule_hub_offline: {
    description: "Scheduled handling for hub-offline events.",
    category: "Devices & Hubs",
  },
  schedule_hub_offline_oban: {
    description: "Hub-offline handling routed through Oban.",
    category: "Devices & Hubs",
  },
  old_version_banner: {
    description:
      "Banner prompting users to refresh when running an old frontend version.",
    category: "UI",
  },
  failed_nodes_cleanup_cron: {
    description: "Failed Z-Wave node cleanup cron job.",
    category: "Devices & Hubs",
  },
  failed_nodes_process_hub_live_run: {
    description: "Live-run mode for failed-node hub processing.",
    category: "Devices & Hubs",
  },
  tours_custom_surveys: {
    description: "Custom surveys for tours.",
    category: "CRM & Tours",
  },
  tours_dashboards_v2: {
    description: "Tours dashboards v2.",
    category: "CRM & Tours",
  },
  dont_skip_states_not_changed_automation_events: {
    description:
      "Processes automation events even when device state has not changed.",
    category: "Platform",
  },
  zipato_check_device_online: {
    description: "Zipato device-online check background job.",
    category: "Devices & Hubs",
  },
  email_template_editor_v2: {
    description: "Email template editor v2 in CMW.",
    category: "UI",
  },
  rotate_duplicate_vault_codes: {
    description: "Background job that rotates duplicate vault codes.",
    category: "Access Control",
  },
  remove_duplicate_dsk_devices_on_pair: {
    description: "Removes duplicate DSK devices during pairing.",
    category: "Devices & Hubs",
  },
  roi_reports: {
    description: "ROI reports in CMW.",
    category: "Reports",
  },
  residents_overview: {
    description: "Residents overview page in CMW.",
    category: "Residents",
  },
  webhooks_use_oban_queue: {
    description: "Routes webhook delivery through the Oban queue.",
    category: "Platform",
  },
  zipato_hmac_authentication: {
    description: "HMAC authentication for Zipato requests.",
    category: "Platform",
  },
  deny_mgmt_api_access_to_api_users: {
    description: "Denies management-API access to API users.",
    category: "Platform",
  },
  resident_t6_paired_on_common_wire_health_check: {
    description: "Unit health check: T6 thermostat paired on common wire.",
    category: "Reports",
  },
  resident_stale_attributes_health_check: {
    description: "Unit health check: stale device attributes.",
    category: "Reports",
  },
  enable_oom_debugger: {
    description: "Out-of-memory debugger for the CMW backend.",
    category: "Platform",
  },
  engrain_pathfinding: {
    description: "Engrain SightMap pathfinding for tours.",
    category: "CRM & Tours",
  },
  sidebar_collapse: {
    description: "Collapsible sidebar UI in CMW.",
    category: "UI",
  },
  task_migration: {
    description: "SMRT Core work-order task migration job.",
    category: "Work / SMRT Core",
  },
  tours_ai_assistant: {
    description: "AI assistant for tours (management API pipeline).",
    category: "CRM & Tours",
  },
  safety_control_settings: {
    description: "Safety control settings for devices and groups.",
    category: "Devices & Hubs",
  },
  humidity_control_settings: {
    description: "Humidity control settings and notifications.",
    category: "Climate",
  },
  humidistat_control_settings: {
    description: "Humidistat control settings for devices and groups.",
    category: "Climate",
  },
  resident_guest_elp_refresh: {
    description: "Resident/guest ELP refresh on unauthenticated access codes.",
    category: "Access Control",
  },
  zipato_remote_device_name_update: {
    description: "Remote device-name updates for Zipato devices.",
    category: "Devices & Hubs",
  },
  auth0_enabled: {
    description: "Auth0 SSO for the organization.",
    category: "Platform",
  },
  cross_pair_log: {
    description: "Cross-pair logging during device discovery.",
    category: "Devices & Hubs",
  },
  aa_schedules: {
    description: "Automated access schedules.",
    category: "Access Control",
  },
  hub_reliability_logging: {
    description: "Hub reliability logging background job.",
    category: "Devices & Hubs",
  },
};
